import {
  Baby, Bone, Brain, Eye, HeartPulse, Stethoscope, Activity, Syringe, Ear,
  Wind, Smile, Leaf, Dumbbell, BriefcaseMedical,
} from 'lucide-react';

const CATALOG = [
  { re: /pedia|child|neonat/i, Icon: Baby, hint: 'Growth, vaccines & childhood illness' },
  { re: /pulmo|lung|respir/i, Icon: Wind, hint: 'Cough, asthma & breathing concerns' },
  { re: /occupational/i, Icon: BriefcaseMedical, hint: 'Workplace fitness & occupational care' },
  { re: /cardio|heart/i, Icon: HeartPulse, hint: 'Heart health & blood pressure' },
  { re: /ortho|bone|joint/i, Icon: Bone, hint: 'Bones, joints & sports injury' },
  { re: /physio.*ortho|ortho.*physio/i, Icon: Dumbbell, hint: 'Rehab, posture & joint recovery' },
  { re: /physio|neuro.*physio|physio.*neuro/i, Icon: Dumbbell, hint: 'Neuro-rehab & recovery therapy' },
  { re: /neuro|brain|psychiatr/i, Icon: Brain, hint: 'Mental health, stress & nerve care' },
  { re: /eye|ophthal|vision/i, Icon: Eye, hint: 'Vision checks & eye conditions' },
  { re: /ent|ear|throat/i, Icon: Ear, hint: 'Ear, nose & throat concerns' },
  { re: /derma|skin/i, Icon: Activity, hint: 'Skin, hair & allergy care' },
  { re: /dental|tooth|oral/i, Icon: Smile, hint: 'Tooth pain & oral health' },
  { re: /homeopath/i, Icon: Leaf, hint: 'Complementary homeopathy care' },
  { re: /gyn|obste|women/i, Icon: Syringe, hint: 'Women’s health & pregnancy' },
  { re: /general|internal|family|medicine|physician/i, Icon: Stethoscope, hint: 'Fever, infections & everyday care' },
];

const FALLBACK = { Icon: Stethoscope, hint: 'Specialist consultation for your needs' };

/** Merge GP aliases so booking shows one everyday-care card. */
export function canonicalizeSpecialty(name = '') {
  const raw = String(name || '').trim() || 'General consultation';
  if (/^(general\s*(physician|medicine|practitioner)|internal\s*medicine|family\s*medicine)$/i.test(raw)) {
    return 'General Physician';
  }
  return raw;
}

export function specialtyVisual(name = '') {
  const hit = CATALOG.find((c) => c.re.test(name));
  return hit ? { Icon: hit.Icon, hint: hit.hint } : FALLBACK;
}

export function groupDoctorsBySpecialty(doctors = []) {
  const map = new Map();
  doctors.forEach((d) => {
    const key = canonicalizeSpecialty(d.specialization || 'General consultation');
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(d);
  });
  return Array.from(map.entries())
    .map(([specialty, list]) => ({ specialty, doctors: list }))
    .sort((a, b) => a.specialty.localeCompare(b.specialty));
}

export function groupDoctorsByCategory(doctors = []) {
  const order = ['Medical Officers', 'Duty Medical Officers', 'Visiting Consultants', 'Physiotherapy', 'Doctors'];
  const map = new Map();
  doctors.forEach((d) => {
    const key = (d.category || 'Doctors').trim() || 'Doctors';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(d);
  });
  return Array.from(map.entries())
    .map(([category, list]) => ({ category, doctors: list }))
    .sort((a, b) => {
      const ai = order.indexOf(a.category);
      const bi = order.indexOf(b.category);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
}

export function specialtiesFromAvailability(avail = []) {
  const map = new Map();
  avail.forEach((a) => {
    const spec = canonicalizeSpecialty(a.specialisation || 'General consultation');
    if (!map.has(spec)) map.set(spec, new Map());
    map.get(spec).set(a.doctor_id, { id: a.doctor_id, name: a.doctor_name, spec });
  });
  return Array.from(map.entries())
    .map(([specialty, docs]) => ({ specialty, doctors: Array.from(docs.values()) }))
    .sort((a, b) => a.specialty.localeCompare(b.specialty));
}
